import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useHashPackWallet } from '../contexts/HashPackWalletContext';
import WalletModal from './WalletModal';

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isConnected, accountId } = useHashPackWallet();

  const isWalletConnected = isConnected && !!accountId;

  // Redirect to chat if wallet is already connected
  useEffect(() => {
    if (isWalletConnected) {
      navigate('/chat');
    }
  }, [isWalletConnected, navigate]);

  const handleConnectWallet = () => {
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Hero Section */}
        <div className="text-center mb-12">
          {/* Logo */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-green-500/20 border-2 border-green-500/30 rounded-full mb-6 p-4">
              <img 
                src="/logo.png" 
                alt="COPYCAT Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-6xl font-bold text-green-400 mb-4 tracking-wider">
              COPYCAT
            </h1>
            <div className="text-green-500/70 text-lg mb-2">V1.0.0</div>
            <div className="w-32 h-0.5 bg-green-500/30 mx-auto"></div>
          </div>

          {/* Hero Text */}
          <div className="mb-12">
            <h2 className="text-3xl font-semibold text-green-300 mb-6">
              Advanced Trading Terminal
            </h2>
            <p className="text-green-400/70 text-lg max-w-2xl mx-auto leading-relaxed">
              Connect your wallet to access professional trading tools, 
              automated strategies, and real-time market analysis in our 
              terminal-style interface.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="p-6 bg-[#0d0d2a] border-green-500/30 hover:border-green-400/50 transition-colors">
              <div className="text-2xl mb-4">📊</div>
              <h3 className="text-green-300 font-semibold mb-2">Real-time Trading</h3>
              <p className="text-green-400/60 text-sm">
                Execute trades with live market data and advanced charting tools
              </p>
            </Card>
            
            <Card className="p-6 bg-[#0d0d2a] border-green-500/30 hover:border-green-400/50 transition-colors">
              <div className="text-2xl mb-4">🤖</div>
              <h3 className="text-green-300 font-semibold mb-2">AI Strategies</h3>
              <p className="text-green-400/60 text-sm">
                Automated trading strategies powered by machine learning
              </p>
            </Card>
            
            <Card className="p-6 bg-[#0d0d2a] border-green-500/30 hover:border-green-400/50 transition-colors">
              <div className="text-2xl mb-4">👥</div>
              <h3 className="text-green-300 font-semibold mb-2">Group Trading</h3>
              <p className="text-green-400/60 text-sm">
                Join trading groups and copy successful strategies
              </p>
            </Card>
          </div>

          {/* Connection Section */}
          <Card className="p-8 bg-[#0d0d2a] border-green-500/30 max-w-md mx-auto">
            <div className="mb-6">
              <div className="text-green-400/70 text-sm mb-2">STEP 1</div>
              <h3 className="text-xl font-semibold text-green-300 mb-4">
                Connect Your Wallet
              </h3>
              <p className="text-green-400/60 text-sm mb-6">
                Secure connection required to access trading features
              </p>
            </div>

            <Button
              onClick={handleConnectWallet}
              className="w-full bg-green-500/20 hover:bg-green-500/30 text-green-400 border-2 border-green-500/30 hover:border-green-400 py-3 text-lg font-semibold transition-all duration-200"
            >
              <span className="mr-3">🔗</span>
              Connect Wallet
            </Button>

            <div className="mt-4 text-xs text-green-400/50">
              Supports MetaMask, WalletConnect, and more
            </div>
          </Card>
        </div>

        {/* Terminal Footer */}
        <div className="text-center text-green-500/50 text-sm">
          <div className="mb-2">
            <span className="text-green-400">$</span> copycat --version 1.0.0
          </div>
          <div>
            Ready to initialize trading session...
          </div>
        </div>
      </div>

      {/* Wallet Modal */}
      <WalletModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default SignupPage;
